"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import {
  ShieldCheck,
  Mail,
  Lock,
  RefreshCw,
  Eye,
  EyeOff,
  ArrowRight,
  CheckCircle2,
  BarChart3,
  Users,
  Gauge,
  AlertTriangle,
  X,
} from "lucide-react";

const recursos = [
  {
    titulo: "Visão 360°",
    texto: "Tenha uma visão completa do que realmente importa.",
    Icon: BarChart3,
  },
  {
    titulo: "Gestão de Equipes",
    texto: "Acompanhe sua equipe com clareza e objetividade.",
    Icon: Users,
  },
  {
    titulo: "Desempenho",
    texto: "Dados e relatórios para decisões mais assertivas.",
    Icon: Gauge,
  },
];

const LOGIN_TIMEOUT_MS = 20000;
const PAPEL_AUTENTICADO_KEY = "bp-auth-papel";
const BOAS_VINDAS_EXPIRA_EM_KEY = "bp-boas-vindas-notificacao-expira-em";
const BOAS_VINDAS_CHANGE_EVENT = "bp:boas-vindas-change";
const BOAS_VINDAS_DURACAO_MS = 10 * 60_000;

function salvarPapelAutenticado(papel?: string | null) {
  if (papel === "USUARIO" || papel === "GESTOR" || papel === "ADM" || papel === "PROGRAMADOR" || papel === "PLATFORM_ADMIN") {
    localStorage.setItem(PAPEL_AUTENTICADO_KEY, papel);
    window.dispatchEvent(new Event("bp-auth-role-change"));
  }
}

export default function Login() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [lembrar, setLembrar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [loginBloqueioDefinitivo, setLoginBloqueioDefinitivo] = useState(false);
  const [entrando, setEntrando] = useState(false);
  const [codigoProgramadorAberto, setCodigoProgramadorAberto] = useState(false);
  const [emailProgramador, setEmailProgramador] = useState("");
  const [codigoProgramador, setCodigoProgramador] = useState("");
  const [erroCodigoProgramador, setErroCodigoProgramador] = useState<string | null>(null);
  const [confirmandoProgramador, setConfirmandoProgramador] = useState(false);
  const [recuperacaoAberta, setRecuperacaoAberta] = useState(false);
  const [recuperacaoEnviada, setRecuperacaoEnviada] = useState(false);
  const [emailRecuperacao, setEmailRecuperacao] = useState("");
  const [erroRecuperacao, setErroRecuperacao] = useState<string | null>(null);
  const [processandoRecuperacao, setProcessandoRecuperacao] = useState(false);

  async function entrar(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !senha) {
      setErro("Preencha email e senha.");
      return;
    }
    setEntrando(true);
    setErro(null);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);
    try {
      const resp = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({ email: email.trim(), senha }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setEntrando(false);
        setLoginBloqueioDefinitivo(Boolean(data.bloqueioDefinitivo));
        setErro(data.erro ?? "Não foi possível entrar.");
        return;
      }
      setLoginBloqueioDefinitivo(false);
      if (data.requerCodigoProgramador) {
        setEntrando(false);
        setEmailProgramador(data.email ?? email.trim().toLowerCase());
        setCodigoProgramador("");
        setErroCodigoProgramador(null);
        setCodigoProgramadorAberto(true);
        return;
      }

      const destino =
        data.papel === "GESTOR"
          ? "/gestor/entrada"
          : data.papel === "ADM"
            ? "/admin/inicio"
            : data.papel === "PROGRAMADOR" || data.papel === "PLATFORM_ADMIN"
              ? "/programador/inicio"
              : "/";
      const notificarBoasVindas =
        data.primeiroAcesso === true &&
        (data.papel === "USUARIO" || data.papel === "GESTOR");
      if (notificarBoasVindas) {
        window.sessionStorage.setItem(BOAS_VINDAS_EXPIRA_EM_KEY, String(Date.now() + BOAS_VINDAS_DURACAO_MS));
      } else {
        window.sessionStorage.removeItem(BOAS_VINDAS_EXPIRA_EM_KEY);
      }
      window.dispatchEvent(new Event(BOAS_VINDAS_CHANGE_EVENT));
      salvarPapelAutenticado(data.papel);
      router.push(destino);
      router.refresh();
      setEntrando(false);
    } catch (error) {
      setEntrando(false);
      if (error instanceof DOMException && error.name === "AbortError") {
        setErro("Tempo de conexao esgotado. Tente novamente.");
        return;
      }
      setErro("Falha de conexão. Tente novamente.");
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function confirmarCodigoProgramador(e: React.FormEvent) {
    e.preventDefault();
    setConfirmandoProgramador(true);
    setErroCodigoProgramador(null);
    try {
      const resp = await fetch("/api/login-programador-codigo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: emailProgramador,
          token: codigoProgramador.trim(),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setErroCodigoProgramador(data.erro ?? "Codigo invalido ou expirado.");
        return;
      }
      salvarPapelAutenticado(data.papel);
      setCodigoProgramadorAberto(false);
      router.push("/programador/inicio");
      router.refresh();
    } catch {
      setErroCodigoProgramador("Falha de conexao. Tente novamente.");
    } finally {
      setConfirmandoProgramador(false);
    }
  }

  function abrirRecuperacao() {
    setEmailRecuperacao(email.trim().toLowerCase());
    setErroRecuperacao(null);
    setRecuperacaoEnviada(false);
    setRecuperacaoAberta(true);
  }

  async function enviarCodigoRecuperacao(e?: React.FormEvent) {
    e?.preventDefault();
    setProcessandoRecuperacao(true);
    setErroRecuperacao(null);
    try {
      const resp = await fetch("/api/recuperar-senha", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailRecuperacao.trim().toLowerCase() }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setErroRecuperacao(data.erro ?? "Nao foi possivel enviar o link.");
        return;
      }
      if (data.aguardeSegundos) {
        setErroRecuperacao(`Solicite um novo link em ${data.aguardeSegundos} segundos.`);
        return;
      }
      setRecuperacaoEnviada(true);
    } catch {
      setErroRecuperacao("Falha de conexao. Tente novamente.");
    } finally {
      setProcessandoRecuperacao(false);
    }
  }

  /* ── conteúdo do formulário (compartilhado mobile/desktop) ─── */
  const formConteudo = (
    <>
      <div
        className="flex h-14 w-14 items-center justify-center rounded-[14px]"
        style={{ background: "#1D4ED8" }}
      >
        <span className="text-[22px] font-extrabold tracking-tight text-white">BP</span>
      </div>

      <h3 className="mt-5 text-[20px] font-bold leading-tight" style={{ color: "#0F172A" }}>
        Bem-vindo de volta
      </h3>
      <p className="mt-1.5 text-[14px]" style={{ color: "#64748B" }}>
        Faça login para continuar
      </p>

      <div className="mt-6 space-y-5">
        {/* Email */}
        <div>
          <label className="mb-2 block text-[12px] font-semibold" style={{ color: "#0F172A" }}>
            Email
          </label>
          <div className="relative">
            <Mail
              className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2"
              style={{ color: "#94A3B8" }}
            />
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@exemplo.com"
              className="h-[52px] w-full rounded-[12px] border border-[#E2E8F0] pl-12 pr-4 text-[14px] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB]"
              style={{ color: "#0F172A" }}
            />
          </div>
        </div>

        {/* Senha */}
        <div>
          <label className="mb-2 block text-[12px] font-semibold" style={{ color: "#0F172A" }}>
            Senha
          </label>
          <div className="relative">
            <Lock
              className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2"
              style={{ color: "#94A3B8" }}
            />
            <input
              type={mostrarSenha ? "text" : "password"}
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Digite sua senha"
              className="h-[52px] w-full rounded-[12px] border border-[#E2E8F0] pl-12 pr-12 text-[14px] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB]"
              style={{ color: "#0F172A" }}
            />
            <button
              type="button"
              onClick={() => setMostrarSenha((v) => !v)}
              className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors hover:opacity-70"
              style={{ color: "#94A3B8" }}
              aria-label={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
            >
              {mostrarSenha ? (
                <EyeOff className="h-[18px] w-[18px]" />
              ) : (
                <Eye className="h-[18px] w-[18px]" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Erro */}
      {erro && (
        <div className="mt-5 rounded-[12px] border border-red-200 bg-red-50 px-3 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500" />
            <p className="text-[12px] text-red-700">{erro}</p>
          </div>
          {loginBloqueioDefinitivo && (
            <button
              type="button"
              onClick={abrirRecuperacao}
              className="mt-3 text-[13px] font-semibold text-[#2563EB] hover:text-[#1D4ED8]"
            >
              Esqueci minha senha
            </button>
          )}
        </div>
      )}

      {/* Lembrar + Esqueci */}
      <div className="mt-5 flex items-center justify-between gap-4">
        <label
          className="flex cursor-pointer items-center gap-2 text-[14px]"
          style={{ color: "#334155" }}
        >
          <input
            type="checkbox"
            checked={lembrar}
            onChange={(e) => setLembrar(e.target.checked)}
            className="h-4 w-4 rounded accent-[#2563EB]"
          />
          Lembrar de mim
        </label>
        <button
          type="button"
          onClick={abrirRecuperacao}
          className="text-[14px] font-semibold transition-colors hover:text-[#1D4ED8]"
          style={{ color: "#2563EB" }}
        >
          Esqueci minha senha
        </button>
      </div>

      {/* Botão entrar */}
      <button
        type="submit"
        disabled={entrando}
        className="mt-6 flex h-[52px] w-full items-center justify-center gap-2 rounded-[12px] bg-[#2563EB] px-4 text-[16px] font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60"
      >
        {entrando ? (
          <RefreshCw className="h-5 w-5 animate-spin" />
        ) : (
          <ArrowRight className="h-5 w-5" />
        )}
        {entrando ? "Entrando..." : "Entrar"}
      </button>

      {/* Rodapé */}
      <p className="mt-8 text-center text-[12px]" style={{ color: "#94A3B8" }}>
        Acesso fornecido pelo administrador.
      </p>
    </>
  );

  return (
    <div
      className="min-h-dvh"
      style={{ background: "linear-gradient(135deg,#0B1F4D 0%,#04102A 100%)" }}
    >
      {/* ─── MOBILE ──────────────────────────────────────────────── */}
      <div className="flex min-h-dvh flex-col items-center justify-center px-6 py-10 lg:hidden">
        <div className="w-full max-w-[400px]">
          <div className="mb-6 flex items-center gap-3">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-[12px]"
              style={{ background: "#1D4ED8" }}
            >
              <span className="text-[18px] font-extrabold tracking-tight text-white">BP</span>
            </div>
            <div>
              <p className="text-[16px] font-semibold leading-tight text-white">BP Acompanhamento</p>
              <p className="text-[12px]" style={{ color: "rgba(255,255,255,0.6)" }}>
                por ACV Vision
              </p>
            </div>
          </div>
          <form
            onSubmit={entrar}
            className="w-full rounded-[24px] bg-white px-6 pb-6 pt-6"
            style={{ border: "1px solid #E5E7EB", boxShadow: "0 20px 60px rgba(15,23,42,0.10)" }}
          >
            {formConteudo}
          </form>
        </div>
      </div>

      {/* ─── DESKTOP ─────────────────────────────────────────────── */}
      <div
        className="hidden lg:grid lg:min-h-dvh lg:items-stretch lg:px-14 lg:py-14 lg:grid-cols-[minmax(0,1fr)_32px_420px] xl:grid-cols-[minmax(0,1fr)_40px_500px] 2xl:grid-cols-[minmax(0,1fr)_48px_560px]"
      >
        {/* HERO */}
        <section
          className="relative flex flex-col overflow-hidden rounded-[24px] px-8 pb-10 pt-8 text-white xl:px-10 xl:pb-14 xl:pt-10"
          style={{
            background: "linear-gradient(180deg,#0C2F84 0%,#08215D 54%,#071441 100%)",
            border: "1px solid #123A8A",
            boxShadow: "0 20px 60px rgba(15,23,42,0.10)",
          }}
        >
          {/* Gradientes decorativos */}
          <div className="absolute inset-y-0 right-[-242px] top-[-96px] w-[848px] rounded-[49%] bg-[radial-gradient(circle_at_26%_48%,rgba(9,32,91,0.92)_0%,rgba(8,24,73,0.98)_46%,rgba(7,20,65,0.98)_63%,rgba(7,20,65,0)_64%)]" />
          <div className="absolute -right-[68px] top-[56px] h-[744px] w-[356px] rounded-[48%] bg-[radial-gradient(circle_at_38%_26%,rgba(59,130,246,0.44)_0%,rgba(37,99,235,0.24)_36%,rgba(37,99,235,0.10)_58%,transparent_74%)]" />
          <div className="absolute inset-y-0 right-0 w-[360px] bg-[radial-gradient(circle_at_72%_42%,rgba(96,165,250,0.15),transparent_58%)]" />
          <div className="absolute bottom-0 right-[58px] h-[250px] w-[356px] overflow-hidden">
            <div className="absolute -bottom-[150px] right-0 h-[320px] w-[320px] rounded-full border border-[#60A5FA]/[0.18]" />
            <div className="absolute -bottom-[126px] right-[34px] h-[274px] w-[274px] rounded-full border border-[#60A5FA]/[0.14]" />
            <div className="absolute -bottom-[102px] right-[68px] h-[226px] w-[226px] rounded-full border border-[#60A5FA]/[0.10]" />
          </div>
          <div className="absolute bottom-[58px] right-[82px] h-[154px] w-[154px] opacity-[0.65] [background-image:radial-gradient(circle,rgba(96,165,250,0.55)_1.2px,transparent_1.2px)] [background-size:12px_12px]" />
          <div className="pointer-events-none absolute right-[154px] top-[242px] opacity-[0.11]">
            <ShieldCheck className="h-[248px] w-[248px] text-[#8FB8FF]" strokeWidth={1.15} />
          </div>

          <div className="relative z-10 flex flex-1 flex-col">
            {/* Logo */}
            <div className="flex items-center gap-4">
              <div
                className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-[14px]"
                style={{ background: "#1D4ED8" }}
              >
                <span className="text-[22px] font-extrabold tracking-tight text-white">BP</span>
              </div>
              <div>
                <p className="text-[22px] font-semibold leading-none text-white">BP Acompanhamento</p>
                <p className="mt-1.5 text-[13px]" style={{ color: "rgba(255,255,255,0.72)" }}>
                  @ por ACV Vision
                </p>
              </div>
            </div>

            {/* Headline */}
            <div className="mt-12 xl:mt-[94px]">
              <h2 className="text-[52px] font-extrabold leading-[0.96] tracking-[-0.03em] text-white xl:text-[72px]">
                Acompanhe.
                <br />
                Gerencie.
                <br />
                <span style={{ color: "#60A5FA" }}>Evolua.</span>
              </h2>
              <div
                className="mt-5 h-[4px] w-[104px] rounded-full xl:mt-6"
                style={{ background: "#2563EB" }}
              />
              <p
                className="mt-6 max-w-[440px] text-[14px] leading-7 xl:mt-8 xl:max-w-[470px] xl:text-[16px] xl:leading-8"
                style={{ color: "#CBD5E1" }}
              >
                Uma plataforma completa para acompanhar clientes, equipe e desempenho em
                tempo real, com inteligência e segurança.
              </p>
            </div>

            {/* Blocos de recursos */}
            <div className="mt-8 grid grid-cols-3 gap-3 xl:mt-12 xl:gap-4">
              {recursos.map(({ titulo, texto, Icon }) => (
                <div
                  key={titulo}
                  className="rounded-[16px] px-3 py-3.5 backdrop-blur-sm xl:px-4 xl:py-4"
                  style={{
                    border: "1px solid rgba(255,255,255,0.10)",
                    background: "rgba(8,28,86,0.24)",
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] text-white xl:h-10 xl:w-10 xl:rounded-[12px]"
                      style={{ background: "#1D4ED8" }}
                    >
                      <Icon className="h-4 w-4 xl:h-5 xl:w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold leading-tight text-white xl:text-[14px]">
                        {titulo}
                      </p>
                      <p
                        className="mt-1 text-[11px] leading-[1.4] xl:text-[12px] xl:leading-5"
                        style={{ color: "#CBD5E1" }}
                      >
                        {texto}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bloco segurança */}
            <div
              className="mt-auto pt-10 max-w-[560px] rounded-[18px] px-4 py-4 backdrop-blur-sm xl:px-5"
              style={{
                border: "1px solid rgba(255,255,255,0.10)",
                background: "rgba(8,28,86,0.34)",
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                  style={{ background: "rgba(255,255,255,0.10)" }}
                >
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-white xl:text-[14px]">
                    Segurança e privacidade são a base de tudo o que fazemos.
                  </p>
                  <p
                    className="mt-1 text-[11px] leading-5 xl:text-[12px]"
                    style={{ color: "#CBD5E1" }}
                  >
                    Seus dados protegidos, seu negócio seguro.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gutter */}
        <div />

        {/* Card de login */}
        <section className="flex items-center">
          <form
            onSubmit={entrar}
            className="w-full rounded-[24px] bg-white px-6 pb-6 pt-6"
            style={{ border: "1px solid #E5E7EB", boxShadow: "0 20px 60px rgba(15,23,42,0.10)" }}
          >
            {formConteudo}
          </form>
        </section>
      </div>

      {codigoProgramadorAberto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Fechar codigo"
            onClick={() => setCodigoProgramadorAberto(false)}
          />
          <form
            onSubmit={confirmarCodigoProgramador}
            className="relative w-full max-w-[420px] rounded-[24px] bg-white p-6 shadow-2xl"
          >
            <button
              type="button"
              onClick={() => setCodigoProgramadorAberto(false)}
              className="absolute right-5 top-5 rounded-full p-1 text-[#64748B] hover:bg-[#F1F5F9]"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-[20px] font-bold text-[#0F172A]">Confirmar acesso</h3>
            <p className="mt-1 text-[14px] text-[#64748B]">
              Enviamos um codigo para {emailProgramador}. Ele vale por 10 minutos.
            </p>
            <label className="mb-2 mt-5 block text-[12px] font-semibold text-[#0F172A]">Codigo</label>
            <input
              value={codigoProgramador}
              onChange={(e) => setCodigoProgramador(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="h-[52px] w-full rounded-[12px] border border-[#E2E8F0] px-4 text-center text-[22px] font-bold tracking-[0.3em] outline-none focus:border-[#2563EB]"
              placeholder="000000"
              inputMode="numeric"
            />
            {erroCodigoProgramador && (
              <p className="mt-4 rounded-[12px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
                {erroCodigoProgramador}
              </p>
            )}
            <button
              type="submit"
              disabled={confirmandoProgramador || codigoProgramador.length < 6}
              className="mt-5 flex h-[48px] w-full items-center justify-center gap-2 rounded-[12px] bg-[#2563EB] text-[15px] font-semibold text-white disabled:opacity-60"
            >
              {confirmandoProgramador && <RefreshCw className="h-4 w-4 animate-spin" />}
              Entrar com codigo
            </button>
          </form>
        </div>
      )}

      {recuperacaoAberta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Fechar recuperacao"
            onClick={() => setRecuperacaoAberta(false)}
          />
          <form
            onSubmit={enviarCodigoRecuperacao}
            className="relative w-full max-w-[420px] rounded-[24px] bg-white p-6 shadow-2xl"
          >
            <button
              type="button"
              onClick={() => setRecuperacaoAberta(false)}
              className="absolute right-5 top-5 rounded-full p-1 text-[#64748B] hover:bg-[#F1F5F9]"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
            {recuperacaoEnviada ? (
              <div className="py-6 text-center">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
                <h3 className="mt-4 text-[20px] font-bold text-[#0F172A]">Verifique seu e-mail</h3>
                <p className="mt-2 text-[14px] text-[#64748B]">
                  Se existir uma conta para {emailRecuperacao.trim().toLowerCase()}, enviaremos um botao para redefinir a senha.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-[20px] font-bold text-[#0F172A]">Recuperar senha</h3>
                <p className="mt-1 text-[14px] text-[#64748B]">
                  Informe seu e-mail para receber o botao de redefinicao.
                </p>

                <label className="mb-2 mt-5 block text-[12px] font-semibold text-[#0F172A]">E-mail</label>
                <input
                  type="email"
                  value={emailRecuperacao}
                  onChange={(e) => setEmailRecuperacao(e.target.value)}
                  className="h-[48px] w-full rounded-[12px] border border-[#E2E8F0] px-4 text-[14px] outline-none focus:border-[#2563EB]"
                  placeholder="nome@exemplo.com"
                />
              </>
            )}

            {erroRecuperacao && (
              <p className="mt-4 rounded-[12px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
                {erroRecuperacao}
              </p>
            )}
            {!recuperacaoEnviada && (
              <button
                type="submit"
                disabled={processandoRecuperacao || !emailRecuperacao.trim()}
                className="mt-5 flex h-[48px] w-full items-center justify-center gap-2 rounded-[12px] bg-[#2563EB] text-[15px] font-semibold text-white disabled:opacity-60"
              >
                {processandoRecuperacao && <RefreshCw className="h-4 w-4 animate-spin" />}
                Enviar link
              </button>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
